import React, { useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate, useLocation } from 'react-router-dom';
import logEvent, { LogEvent, LogEventStatus } from 'utils/logEvent';
import urlcat from 'urlcat';
import queryString from 'query-string';
import useHandleAuthErrors from 'utils/useHandleAuthErrors';
import useSearchParams from 'utils/useQueryParams';
import config from 'utils/config';
import { getIdpFromWebfinger } from 'utils/webfinger';
import SignInForm from 'pages/Login/SignInForm';
import {
  setUserNameCookie,
  clearUserNameCookie,
  getUserNameFromCookie,
} from 'utils/cookies';

const getIdpForUser = getIdpFromWebfinger(config.oneIdManagementApi);

export interface LoginValues {
  username: string;
  password: string;
  rememberme: boolean;
}

const SignIn: React.FC = () => {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const [{ pwd }] = useSearchParams();
  const [errorMessage, setErrorMessage] = useHandleAuthErrors();
  const [loading, setLoading] = useState(false);
  const [userNameState, setUserNameState] = useState(() => {
    return getUserNameFromCookie();
  });

  const location = useLocation();
  const { state } = location;

  useEffect(() => {
    if (state?.username) {
      setUserNameState(state.username);
    }
  }, [state]);

  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);

  const handleIdPLookup = useCallback(
    async (username?: string) => {
      const idpInfo = await getIdpForUser(username);
      // Redirect to confirm page if multiple login options exist
      if (idpInfo?.multipleLinks) {
        navigate(
          urlcat(`/login/sso/confirm`, {
            ...queryString.parse(window.location.search),
          }),
          {
            state: {
              username,
              idpId: idpInfo.idpId,
            },
          },
        );
        return true;
      }
      if (idpInfo?.idpId) {
        oktaAuth.signInWithRedirect({
          // (redirect options should be able to have idp)
          idp: idpInfo.idpId,
          loginHint: username,
        });
        return true;
      }

      return false;
    },
    [oktaAuth, navigate],
  );

  const handleChangeUserName = (nameFromForm: string) => {
    setUserNameState(nameFromForm);
    setUserNameCookie(nameFromForm);
  };

  useEffect(() => {
    (async () => {
      // you really only want to show the password field here if the user is on /login?pwd
      setShowPasswordField(pwd !== undefined && !!userNameState);
    })();
  }, [pwd, userNameState]);

  const handleLogin = async ({
    username,
    password,
    rememberme,
  }: LoginValues) => {
    // only call webfinger if they're not entering their password
    if (username && !showPasswordField) {
      const idp = await handleIdPLookup(username);
      if (idp) {
        return;
      }
    }

    handleChangeUserName(username);

    // Handle 'Remember Me'
    if (!rememberme) {
      // expire cookie immediately
      clearUserNameCookie(username);
    }

    if (!showPasswordField) {
      // Display password field if no external IdP
      setErrorMessage(null);
      navigate(
        urlcat(`/login`, {
          pwd: '',
          ...queryString.parse(window.location.search),
        }),
      );
      return;
    }

    try {
      setLoading(true);
      // Get sessionToken with username and password
      const { sessionToken } = await oktaAuth.signInWithCredentials({
        username,
        password,
      });

      // Use the session token to generate ID and access tokens
      const { tokens } = await oktaAuth.token.getWithoutPrompt({
        sessionToken,
        responseType: ['id_token', 'token'],
        scopes: ['openid', 'oneid', 'email', 'profile'],
      });

      // Log successful login
      logEvent(LogEvent.Login, LogEventStatus.Successful, { username });
      datadogRum.addAction('login', { username });

      localStorage.removeItem('attempts');
      await oktaAuth.handleLoginRedirect(tokens);
      // TODO: Clean up error type – figure out if this is functional.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setErrorMessage(error, username);
      setLoading(false);
    }
  };

  return (
    <SignInForm
      formikConfig={{
        onSubmit: handleLogin,
        validate: (values) => {
          const errors: Record<string, string> = {};
          if (values.username?.length <= 0) {
            errors.username = 'Username is required';
          }
          if (showPasswordField && values.password?.length <= 0) {
            errors.password = 'Password is required';
          }
          return errors;
        },
        enableReinitialize: true,
        initialValues: {
          username: userNameState ?? getUserNameFromCookie(),
          password: '',
          rememberme: !!getUserNameFromCookie(),
        },
      }}
      errorMessage={errorMessage}
      loading={loading}
      setErrorMessage={setErrorMessage}
      showPasswordField={showPasswordField}
    />
  );
};
export default SignIn;
